/**
 * Landing.jsx — Rently.ma Public Landing Page
 * BRAND-THEME-004: Ivory-led, blue-primary, no accent colors
 *
 * Light/ivory hero, scroll animations, subtle geometric accents
 * Positions Rently as the operating system for Moroccan car rental agencies
 * No fake stats, no fake testimonials, no fake logos
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// ─── Design Tokens (Brand Palette v3 — Blue-Light) ──────────────────────────

const T = {
  // Brand core
  atlasNight:   '#071521',
  primaryBlue:  '#3454B3',
  deepBlue:     '#091D68',
  ivory:        '#F5F1E7',
  stoneSand:    '#D8CCB7',

  // Website surfaces
  bg:           '#F5F1E7',
  bgWhite:      '#ffffff',
  bgDark:       '#071521',
  bgDarkCard:   '#0d2233',
  surface:      '#ffffff',
  surfaceAlt:   '#F5F1E7',

  // Primary / accent
  accent:       '#3454B3',
  accentHover:  '#091D68',

  // Text
  text:         '#071521',
  textSec:      '#3a4a5c',
  textMuted:    '#6b7280',
  white:        '#ffffff',
  lightText:    'rgba(255,255,255,0.75)',

  // Borders
  border:       'rgba(216,204,183,0.6)',
  borderLight:  'rgba(216,204,183,0.35)',
  borderDark:   'rgba(255,255,255,0.10)',

  // Shadows
  sh1: '0 1px 2px rgba(7,21,33,0.04)',
  sh2: '0 4px 16px rgba(7,21,33,0.06)',
  sh3: '0 8px 30px rgba(7,21,33,0.08)',
  sh4: '0 12px 40px rgba(7,21,33,0.10)',

  // Type scale
  fontBase: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  fontMono: "'SF Mono', 'Fira Code', 'Courier New', monospace",

  // Radius
  r1: '8px',
  r2: '12px',
  r3: '16px',
  r4: '20px',
  rFull: '9999px',
};

// ─── Icons (SVG line icons, 20px, 1.5px stroke) ────────────────────────────

const Icon = ({ d, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {typeof d === 'string' ? <path d={d} /> : d}
  </svg>
);

const Icons = {
  // Module icons
  users: <Icon d={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>} />,
  shield: <Icon d={<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>} />,
  camera: <Icon d={<><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></>} />,
  activity: <Icon d={<><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></>} />,
  link: <Icon d={<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>} />,
  card: <Icon d={<><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></>} />,

  // Utility icons
  truck: <Icon d={<><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></>} />,
  file: <Icon d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>} />,
  mapPin: <Icon d={<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>} />,
  check: <Icon d={<><polyline points="20 6 9 17 4 12"/></>} size={16} />,
  arrow: <Icon d={<><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>} size={16} />,
  zap: <Icon d={<><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>} />,
  layers: <Icon d={<><polygon points="12 2 2 7 12 12 22 7 12 2"/><line x1="2" y1="17" x2="12" y2="22"/><line x1="22" y1="17" x2="12" y2="22"/><line x1="2" y1="12" x2="12" y2="17"/><line x1="22" y1="12" x2="12" y2="17"/></>} />,
  alertTriangle: <Icon d={<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>} />,
  clock: <Icon d={<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>} />,
  messageCircle: <Icon d={<><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></>} />,
  whatsapp: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
};

// WhatsApp number — replace with production number
const WA_LINK = 'https://wa.me/34631204625?text=Bonjour%2C%20je%20souhaite%20une%20d%C3%A9mo%20de%20Rently.ma';

// ─── Scroll Animation Hook ──────────────────────────────────────────────────

function useScrollReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, delay = 0, style = {} }) {
  const ref = useScrollReveal();
  return (
    <div ref={ref} style={{
      opacity: 0,
      transform: 'translateY(30px)',
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

// ─── Six Modules Data ───────────────────────────────────────────────────────

// Module card variants: 'soft-blue' | 'featured' | 'white' | 'gradient' | 'gradient-dark'
const MODULE_VARIANTS = {
  'soft-blue': {
    bg: 'rgba(52,84,179,0.06)',
    border: 'rgba(52,84,179,0.10)',
    titleColor: T.atlasNight,
    descColor: T.textSec,
    iconBg: 'rgba(52,84,179,0.10)',
    iconColor: T.primaryBlue,
  },
  featured: {
    bg: T.ivory,
    border: T.stoneSand,
    titleColor: T.atlasNight,
    descColor: T.textSec,
    iconBg: 'rgba(52,84,179,0.10)',
    iconColor: T.primaryBlue,
  },
  white: {
    bg: T.bgWhite,
    border: T.border,
    titleColor: T.atlasNight,
    descColor: T.textSec,
    iconBg: 'rgba(52,84,179,0.08)',
    iconColor: T.primaryBlue,
  },
  gradient: {
    bg: `linear-gradient(135deg, ${T.primaryBlue} 0%, ${T.deepBlue} 100%)`,
    border: 'transparent',
    titleColor: '#ffffff',
    descColor: 'rgba(255,255,255,0.80)',
    iconBg: 'rgba(255,255,255,0.15)',
    iconColor: '#ffffff',
  },
  'gradient-dark': {
    bg: `linear-gradient(135deg, ${T.deepBlue} 0%, ${T.atlasNight} 100%)`,
    border: 'transparent',
    titleColor: '#ffffff',
    descColor: 'rgba(255,255,255,0.75)',
    iconBg: 'rgba(255,255,255,0.12)',
    iconColor: '#ffffff',
  },
};

const MODULES = [
  { icon: Icons.users,    titleKey: 'landing.modules.m0_title', descKey: 'landing.modules.m0_desc', variant: 'soft-blue' },
  { icon: Icons.shield,   titleKey: 'landing.modules.m1_title', descKey: 'landing.modules.m1_desc', variant: 'featured' },
  { icon: Icons.camera,   titleKey: 'landing.modules.m2_title', descKey: 'landing.modules.m2_desc', variant: 'white' },
  { icon: Icons.activity, titleKey: 'landing.modules.m3_title', descKey: 'landing.modules.m3_desc', variant: 'gradient' },
  { icon: Icons.link,     titleKey: 'landing.modules.m4_title', descKey: 'landing.modules.m4_desc', variant: 'gradient-dark' },
  { icon: Icons.card,     titleKey: 'landing.modules.m5_title', descKey: 'landing.modules.m5_desc', variant: 'white' },
];

// ─── Language Switch ────────────────────────────────────────────────────────
// Reuses the same i18n system as the product UI (localStorage 'rently_lang').
// Changes landing content via landing.* namespace; AR gets RTL layout.

function LangSwitch({ dark = false }) {
  const { i18n } = useTranslation();
  const active = (i18n.language || 'fr').substring(0, 2);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 1 }} role="group" aria-label="Langue / Language">
      {['fr', 'ar', 'en'].map(code => (
        <button
          key={code}
          onClick={() => i18n.changeLanguage(code)}
          aria-pressed={active === code}
          style={{
            fontSize: 11,
            fontWeight: active === code ? 700 : 500,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            padding: '5px 9px',
            background: active === code
              ? (dark ? 'rgba(255,255,255,0.12)' : 'rgba(52,84,179,0.10)')
              : 'transparent',
            color: active === code
              ? (dark ? '#ffffff' : T.primaryBlue)
              : (dark ? 'rgba(255,255,255,0.42)' : T.textMuted),
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontFamily: T.fontBase,
            transition: 'all 0.15s',
            minWidth: 32,
            lineHeight: 1,
          }}
          onMouseEnter={e => { if (active !== code) e.currentTarget.style.color = dark ? 'rgba(255,255,255,0.75)' : T.textSec; }}
          onMouseLeave={e => { if (active !== code) e.currentTarget.style.color = dark ? 'rgba(255,255,255,0.42)' : T.textMuted; }}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────

export default function Landing({ onNavigate }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const { t, i18n } = useTranslation();

  // ESC to close login modal
  useEffect(() => {
    if (!loginModalOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setLoginModalOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [loginModalOpen]);

  // Apply correct direction for each language including Arabic RTL.
  useEffect(() => {
    const applyLandingDir = (lng) => {
      const lang = (lng || 'fr').substring(0, 2);
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lang;
    };
    applyLandingDir(i18n.language);
    i18n.on('languageChanged', applyLandingDir);
    return () => {
      i18n.off('languageChanged', applyLandingDir);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: T.bg,
      color: T.text,
      fontFamily: T.fontBase,
      overflowX: 'hidden',
    }}>
      {/* Skip to content — accessibility (transform-based hide, RTL-safe: no left:-9999 overflow) */}
      <a href="#main-content" style={{
        position: 'fixed',
        insetInlineStart: 16,
        top: 8,
        transform: 'translateY(-120px)',
        padding: '10px 20px',
        background: T.primaryBlue,
        color: '#fff',
        borderRadius: T.r1,
        fontWeight: 600,
        fontSize: 13,
        textDecoration: 'none',
        zIndex: 9999,
        transition: 'transform 0.15s',
        fontFamily: T.fontBase,
      }}
      onFocus={e => { e.target.style.transform = 'translateY(0)'; }}
      onBlur={e => { e.target.style.transform = 'translateY(-120px)'; }}
      >Skip to content</a>

      <style>{`
        .btn-accent {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          font-size: 15px;
          font-weight: 600;
          font-family: ${T.fontBase};
          background: ${T.primaryBlue};
          color: ${T.white};
          border: none;
          border-radius: ${T.r2};
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
        }
        .btn-accent:hover { background: ${T.deepBlue}; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(52,84,179,0.25); }
        .btn-accent:focus-visible { outline: 2px solid ${T.primaryBlue}; outline-offset: 2px; }

        .btn-outline-light {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          font-size: 15px;
          font-weight: 600;
          font-family: ${T.fontBase};
          background: transparent;
          color: ${T.text};
          border: 1px solid ${T.border};
          border-radius: ${T.r2};
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
        }
        .btn-outline-light:hover {
          background: rgba(52,84,179,0.06);
          border-color: ${T.primaryBlue};
          color: ${T.primaryBlue};
        }
        .btn-outline-light:focus-visible { outline: 2px solid ${T.primaryBlue}; outline-offset: 2px; }

        .btn-outline-dark {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          font-size: 15px;
          font-weight: 600;
          font-family: ${T.fontBase};
          background: transparent;
          color: ${T.text};
          border: 1px solid ${T.border};
          border-radius: ${T.r2};
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
        }
        .btn-outline-dark:hover {
          border-color: ${T.primaryBlue};
          color: ${T.primaryBlue};
          background: rgba(52,84,179,0.06);
        }
        .btn-outline-dark:focus-visible { outline: 2px solid ${T.primaryBlue}; outline-offset: 2px; }

        .btn-white {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          font-size: 15px;
          font-weight: 600;
          font-family: ${T.fontBase};
          background: ${T.white};
          color: ${T.atlasNight};
          border: none;
          border-radius: ${T.r2};
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
        }
        .btn-white:hover { background: ${T.ivory}; transform: translateY(-1px); }
        .btn-white:focus-visible { outline: 2px solid ${T.white}; outline-offset: 2px; }

        /* Focus states */
        a:focus-visible { outline: 2px solid ${T.primaryBlue}; outline-offset: 2px; border-radius: 4px; }
        button:focus-visible { outline: 2px solid ${T.primaryBlue}; outline-offset: 2px; }

        /* Card lift hover */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding-left: clamp(20px, 5vw, 40px);
          padding-right: clamp(20px, 5vw, 40px);
        }

        .section-pad {
          padding-top: clamp(36px, 5vw, 60px);
          padding-bottom: clamp(36px, 5vw, 60px);
        }

        /* Card lift */
        .card-lift {
          transition: border-color 0.2s ease, box-shadow 0.3s ease, transform 0.3s ease;
        }
        .card-lift:hover {
          box-shadow: 0 8px 30px rgba(0,0,0,0.08);
          transform: translateY(-3px);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .hero-zellige { background-position: 75% center !important; opacity: 0.28 !important; }
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-mock { margin-top: 16px; }
          .two-col { grid-template-columns: 1fr !important; }
          .three-col { grid-template-columns: 1fr !important; }
          .module-grid { grid-template-columns: 1fr !important; }
          .module-featured { grid-column: span 1 !important; }
          .reason-grid { grid-template-columns: 1fr !important; }
          .footer-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .cta-buttons { flex-direction: column !important; align-items: stretch !important; }
          .cta-buttons a, .cta-buttons button { width: 100% !important; justify-content: center; }
          .hero-ctas { flex-direction: column; align-items: stretch !important; }
          .hero-ctas a, .hero-ctas button { width: 100%; justify-content: center; }
          .hide-mobile { display: none !important; }
          .show-mobile { display: flex !important; }
          .pain-grid { grid-template-columns: 1fr !important; }
          .trust-strip { grid-template-columns: 1fr !important; }
          .six-grid { grid-template-columns: 1fr !important; }
        }

        @media (min-width: 481px) and (max-width: 768px) {
          .pain-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .six-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }

        @media (min-width: 769px) {
          .show-mobile { display: none !important; }
        }

      `}</style>

      {/* ═══════════════════════════════════════════════════════════════════
          HEADER — transparent on hero, solid on scroll
          ═══════════════════════════════════════════════════════════════════ */}
      <header style={{
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: 100,
        background: T.bg,
        borderBottom: scrolled ? `1px solid ${T.border}` : '1px solid transparent',
        transition: 'border-color 0.3s ease',
      }}>
        <nav className="container" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 56,
        }}>
          <a href="#" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src="/brand/rently-lockup-teal.svg" alt="Rently.ma"
              style={{ height: 'clamp(36px, 5vw, 42px)', width: 'auto' }} />
          </a>

          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            {/* Nav links — desktop only */}
            {[
              { label: t('landing.nav.modules'), href: '#modules' },
              { label: t('landing.nav.whyRently'), href: '#why-rently' },
              { label: t('landing.nav.contact'), href: '#contact' },
            ].map((item, i) => (
              <a key={i} href={item.href} className="hide-mobile"
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: T.textSec,
                  textDecoration: 'none',
                  padding: '10px 14px',
                  borderRadius: T.r1,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.target.style.color = T.primaryBlue; e.target.style.background = 'rgba(52,84,179,0.06)'; }}
                onMouseLeave={e => { e.target.style.color = T.textSec; e.target.style.background = 'transparent'; }}
              >{item.label}</a>
            ))}

            {/* Language switch — desktop */}
            <div className="hide-mobile" style={{ display: 'flex', alignItems: 'center', marginRight: 4 }}>
              <LangSwitch />
            </div>

            {/* Login link — desktop */}
            <button className="hide-mobile"
              onClick={() => setLoginModalOpen(true)}
              style={{
                fontSize: 13, fontWeight: 600, fontFamily: T.fontBase,
                color: T.textSec, background: 'transparent',
                border: `1px solid ${T.border}`, borderRadius: T.r1,
                padding: '9px 18px', cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.target.style.color = T.primaryBlue; e.target.style.borderColor = T.primaryBlue; e.target.style.background = 'rgba(52,84,179,0.05)'; }}
              onMouseLeave={e => { e.target.style.color = T.textSec; e.target.style.borderColor = T.border; e.target.style.background = 'transparent'; }}
            >
              {t('landing.nav.login')}
            </button>

            {/* Header CTA — desktop */}
            <button className="btn-accent hide-mobile"
              onClick={() => window.location.href = 'mailto:contact@rently.ma?subject=Demande de démo'}
              style={{ padding: '10px 24px', fontSize: 13 }}>
              {t('landing.nav.demoBtn')}
            </button>

            {/* Language switch — mobile top bar (visible without opening menu) */}
            <div className="show-mobile" style={{ display: 'none', alignItems: 'center' }}>
              <LangSwitch />
            </div>

            {/* Mobile hamburger */}
            <button
              className="show-mobile"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                background: 'transparent',
                border: `1px solid ${T.border}`,
                borderRadius: T.r1,
                cursor: 'pointer',
                padding: 0,
                flexDirection: 'column',
                gap: mobileMenuOpen ? 0 : 5,
              }}>
              <span style={{
                display: 'block', width: 18, height: 2, background: T.atlasNight, borderRadius: 1,
                transition: 'all 0.2s ease',
                transform: mobileMenuOpen ? 'rotate(45deg) translateY(1px)' : 'none',
              }} />
              {!mobileMenuOpen && <span style={{ display: 'block', width: 18, height: 2, background: T.atlasNight, borderRadius: 1 }} />}
              <span style={{
                display: 'block', width: 18, height: 2, background: T.atlasNight, borderRadius: 1,
                transition: 'all 0.2s ease',
                transform: mobileMenuOpen ? 'rotate(-45deg) translateY(-1px)' : 'none',
              }} />
            </button>
          </div>
        </nav>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div style={{
            background: T.bg,
            borderBottom: `1px solid ${T.border}`,
            padding: '12px 0',
          }}>
            <div className="container" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[
                { label: t('landing.nav.modules'), href: '#modules' },
                { label: t('landing.nav.whyRently'), href: '#why-rently' },
                { label: t('landing.nav.contact'), href: '#contact' },
              ].map((item, i) => (
                <a key={i} href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: T.textSec,
                    textDecoration: 'none',
                    padding: '12px 16px',
                    borderRadius: T.r1,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.target.style.color = T.primaryBlue; e.target.style.background = 'rgba(52,84,179,0.06)'; }}
                  onMouseLeave={e => { e.target.style.color = T.textSec; e.target.style.background = 'transparent'; }}
                >{item.label}</a>
              ))}
              {/* Language switch — mobile menu */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px 16px',
                borderTop: `1px solid ${T.borderLight}`,
                marginTop: 8,
              }}>
                <LangSwitch />
              </div>

              <button className="btn-accent"
                onClick={() => { setMobileMenuOpen(false); window.location.href = 'mailto:contact@rently.ma?subject=Demande de démo'; }}
                style={{ marginTop: 4, justifyContent: 'center' }}>
                {t('landing.nav.demoBtn')}
              </button>
              <button className="btn-outline-light"
                onClick={() => { setMobileMenuOpen(false); setLoginModalOpen(true); }}
                style={{ marginTop: 4, justifyContent: 'center' }}>
                {t('landing.nav.login')}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ═══════════════════════════════════════════════════════════════════
          HERO — Dark, full-bleed
          ═══════════════════════════════════════════════════════════════════ */}
      <section id="main-content" style={{
        background: T.bg,
        paddingTop: 'clamp(80px, 10vw, 120px)',
        paddingBottom: 'clamp(40px, 6vw, 72px)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Hero background — zellige pattern */}
        <div aria-hidden="true" className="hero-zellige" style={{
          position: 'absolute',
          inset: 0,
          background: `url('/brand/hero-bg.jpg') no-repeat right center`,
          backgroundSize: 'cover',
          opacity: 0.22,
          pointerEvents: 'none',
        }} />

        <div className="container">
          <div className="hero-grid" style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.1fr',
            gap: 'clamp(32px, 6vw, 64px)',
            alignItems: 'center',
          }}>
            {/* Left — Copy */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 28, position: 'relative', zIndex: 1 }}>
              {/* Badge */}
              <Reveal>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 14px',
                  background: 'rgba(52,84,179,0.08)',
                  border: `1px solid rgba(52,84,179,0.15)`,
                  borderRadius: T.rFull,
                  alignSelf: 'flex-start',
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.primaryBlue, boxShadow: `0 0 6px ${T.primaryBlue}` }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.primaryBlue, letterSpacing: '0.5px' }}>
                    {t('landing.hero.badge')}
                  </span>
                </div>
              </Reveal>

              {/* Headline */}
              <Reveal delay={0.1}>
                <h1 style={{
                  fontSize: 'clamp(2.4rem, 5.5vw, 3.6rem)',
                  fontWeight: 800,
                  lineHeight: 1.08,
                  letterSpacing: '-0.03em',
                  margin: 0,
                  color: T.atlasNight,
                }}>
                  {t('landing.hero.title1')}{' '}
                  <span style={{ color: T.primaryBlue }}>{t('landing.hero.titleHighlight')}</span>{' '}
                  {t('landing.hero.title2')}
                </h1>
              </Reveal>

              {/* Subheadline */}
              <Reveal delay={0.2}>
                <p style={{
                  fontSize: 'clamp(16px, 2vw, 18px)',
                  color: T.textSec,
                  lineHeight: 1.65,
                  maxWidth: 520,
                  margin: 0,
                }}>
                  {t('landing.hero.subtitle')}
                </p>
              </Reveal>

              {/* CTAs */}
              <Reveal delay={0.3}>
                <div className="hero-ctas" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 4 }}>
                  <button className="btn-accent"
                    onClick={() => window.location.href = 'mailto:contact@rently.ma?subject=Demande de démo'}>
                    {t('landing.hero.ctaPrimary')} <span style={{ fontSize: 14 }}>→</span>
                  </button>
                  <a href="#modules" className="btn-outline-light"
                    style={{ textDecoration: 'none' }}>
                    {t('landing.hero.ctaSecondary')}
                  </a>
                </div>
              </Reveal>
            </div>

            {/* Right — Real Product Screenshot (desktop only) */}
            <Reveal delay={0.2}>
              <div className="hero-mock">

                {/* Desktop screenshot inside browser chrome frame */}
                <div style={{
                  background: '#0f111a',
                  borderRadius: T.r2,
                  overflow: 'hidden',
                  boxShadow: `0 2px 0 ${T.borderDark}, 0 32px 80px rgba(0,0,0,0.55)`,
                  border: `1px solid ${T.borderDark}`,
                }}>
                  {/* Browser chrome bar */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 14px',
                    gap: 6,
                    background: '#161a26',
                    borderBottom: `1px solid ${T.borderDark}`,
                  }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ffbd2e', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#28c840', display: 'inline-block', flexShrink: 0 }} />
                    <div style={{
                      flex: 1,
                      marginLeft: 10,
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 4,
                      padding: '4px 12px',
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.32)',
                      fontFamily: T.fontBase,
                      letterSpacing: 0.2,
                      lineHeight: 1,
                    }}>
                      app.rently.ma
                    </div>
                  </div>
                  {/* Screenshot — full image, natural aspect, no crop */}
                  <img
                    className="hero-mock-inner"
                    src="/brand/screenshots/dashboard-desktop.webp"
                    alt={t('landing.productProof.desktopAlt')}
                    width="1440"
                    height="900"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                    loading="eager"
                  />
                </div>

              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          TRUST STRIP — Concise signals
          ═══════════════════════════════════════════════════════════════════ */}
      <section style={{
        background: T.bgWhite,
        borderTop: `1px solid ${T.border}`,
        borderBottom: `1px solid ${T.border}`,
        padding: 'clamp(28px, 4vw, 44px) 0',
      }}>
        <div className="container">
          <div className="trust-strip" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'clamp(10px, 1.5vw, 14px)',
          }}>
            {[
              t('landing.trust.item0'),
              t('landing.trust.item1'),
              t('landing.trust.item2'),
              t('landing.trust.item3'),
            ].map((text, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: 'clamp(14px, 1.5vw, 20px)',
                background: T.bg,
                border: `1px solid ${T.border}`,
                borderRadius: T.r2,
              }}>
                <span style={{ color: T.primaryBlue, display: 'flex', flexShrink: 0, marginTop: 1 }}>{Icons.check}</span>
                <span style={{ fontSize: 13, color: T.text, fontWeight: 600, lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          PAIN POINTS — La réalité quotidienne sans système
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="section-pad" style={{ background: T.bgWhite }}>
        <div className="container">
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 4vw, 36px)' }}>
              <p style={{
                fontSize: 11,
                fontWeight: 700,
                color: T.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: 0,
              }}>
                {t('landing.pain.label')}
              </p>
            </div>
          </Reveal>

          <div className="pain-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'clamp(12px, 2vw, 20px)',
          }}>
            {[
              { problem: t('landing.pain.p0_title'), detail: t('landing.pain.p0_desc') },
              { problem: t('landing.pain.p1_title'), detail: t('landing.pain.p1_desc') },
              { problem: t('landing.pain.p2_title'), detail: t('landing.pain.p2_desc') },
              { problem: t('landing.pain.p3_title'), detail: t('landing.pain.p3_desc') },
              { problem: t('landing.pain.p4_title'), detail: t('landing.pain.p4_desc') },
              { problem: t('landing.pain.p5_title'), detail: t('landing.pain.p5_desc') },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.06} style={{ height: '100%' }}>
                <div style={{
                  padding: 'clamp(16px, 2.5vw, 22px)',
                  background: T.bgWhite,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.r2,
                  borderTop: `3px solid rgba(52,84,179,0.22)`,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  boxSizing: 'border-box',
                }}>
                  <div style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: T.atlasNight,
                    marginBottom: 10,
                    lineHeight: 1.35,
                    minHeight: '2.7em',
                  }}>{item.problem}</div>
                  <p style={{
                    fontSize: 13,
                    color: T.textMuted,
                    lineHeight: 1.6,
                    margin: 0,
                    flex: 1,
                  }}>{item.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          WHAT RENTLY ACTUALLY IS — Identity section
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="section-pad" style={{ background: T.bg }}>
        <div className="container" style={{ maxWidth: 800, textAlign: 'center' }}>
          <Reveal>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 14px',
              background: 'rgba(52,84,179,0.08)',
              borderRadius: T.rFull,
              marginBottom: 24,
              color: T.primaryBlue,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}>{Icons.layers} <span>{t('landing.identity.badge')}</span></div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 style={{
              fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
              fontWeight: 800,
              color: T.atlasNight,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              margin: '0 0 24px 0',
            }}>
              {t('landing.identity.title1')} <span style={{ color: T.primaryBlue }}>{t('landing.identity.titleHighlight')}</span>
            </h2>
          </Reveal>

          <Reveal delay={0.2}>
            <p style={{
              fontSize: 'clamp(16px, 2vw, 19px)',
              color: T.textSec,
              lineHeight: 1.7,
              margin: '0 0 16px 0',
            }}>
              {t('landing.identity.p1')}
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <p style={{
              fontSize: 15,
              color: T.textMuted,
              lineHeight: 1.65,
              margin: 0,
            }}>
              {t('landing.identity.p2')}
            </p>
          </Reveal>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════
          SIX MODULES — The core product
          ═══════════════════════════════════════════════════════════════════ */}
      <section id="modules" style={{ background: T.bgWhite }} className="section-pad">
        <div className="container">
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 'clamp(28px, 4vw, 40px)' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 14px',
                background: 'rgba(184,92,56,0.08)',
                borderRadius: T.rFull,
                marginBottom: 16,
                color: T.deepBlue,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}>{Icons.zap} <span>{t('landing.modules.badge')}</span></div>

              <h2 style={{
                fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                fontWeight: 800,
                color: T.atlasNight,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                margin: 0,
              }}>{t('landing.modules.title')} <span style={{ color: T.primaryBlue }}>{t('landing.modules.titleHighlight')}</span></h2>
              <p style={{
                fontSize: 16,
                color: T.textSec,
                margin: '14px auto 0',
                maxWidth: 560,
                lineHeight: 1.65,
              }}>{t('landing.modules.subtitle')}</p>
            </div>
          </Reveal>

          <div className="six-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
          }}>
            {MODULES.map((m, i) => {
              const v = MODULE_VARIANTS[m.variant] || MODULE_VARIANTS.white;
              const isGradient = m.variant === 'gradient' || m.variant === 'gradient-dark';
              return (
              <Reveal key={i} delay={i * 0.08}>
                <div className="card-lift" style={{
                  background: v.bg,
                  padding: 'clamp(28px, 4vw, 36px)',
                  borderRadius: T.r4,
                  border: `1px solid ${v.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 16,
                  cursor: 'default',
                  height: '100%',
                }}
                onMouseEnter={e => { if (!isGradient) e.currentTarget.style.borderColor = T.primaryBlue + '40'; }}
                onMouseLeave={e => { if (!isGradient) e.currentTarget.style.borderColor = v.border; }}
                >
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: T.r3,
                    background: v.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: v.iconColor,
                    transform: 'scale(1)',
                  }}>
                    <span style={{ display: 'flex', transform: 'scale(1.6)' }}>{m.icon}</span>
                  </div>

                  <h3 style={{ fontSize: 17, fontWeight: 700, color: v.titleColor, margin: 0 }}>{t(m.titleKey)}</h3>
                  <p style={{ fontSize: 14, color: v.descColor, lineHeight: 1.6, margin: 0 }}>{t(m.descKey)}</p>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          WHY RENTLY — Ivory section, two-column with sticky heading
          ═══════════════════════════════════════════════════════════════════ */}
      <section id="why-rently" className="section-pad" style={{ background: T.bg }}>
        <div className="container">
          <div className="two-col" style={{
            display: 'grid',
            gridTemplateColumns: '0.9fr 1.1fr',
            gap: 'clamp(40px, 6vw, 72px)',
            alignItems: 'start',
          }}>
            {/* Left — Heading */}
            <Reveal>
              <div style={{ position: 'sticky', top: 100 }}>
                <h2 style={{
                  fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                  fontWeight: 800,
                  color: T.atlasNight,
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  margin: 0,
                }}>
                  {t('landing.why.title1')}{' '}
                  <span style={{ color: T.primaryBlue }}>{t('landing.why.titleHighlight')}</span>&nbsp;?
                </h2>

                <p style={{
                  fontSize: 16,
                  color: T.textSec,
                  lineHeight: 1.65,
                  margin: '20px 0 0 0',
                  maxWidth: 400,
                }}>
                  {t('landing.why.subtitle')}
                </p>
              </div>
            </Reveal>

            {/* Right — Reason cards */}
            <div className="reason-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
              {[
                { num: '01', title: t('landing.why.r0_title'), desc: t('landing.why.r0_desc') },
                { num: '02', title: t('landing.why.r1_title'), desc: t('landing.why.r1_desc') },
                { num: '03', title: t('landing.why.r2_title'), desc: t('landing.why.r2_desc') },
                { num: '04', title: t('landing.why.r3_title'), desc: t('landing.why.r3_desc') },
              ].map((r, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div style={{
                    padding: 'clamp(20px, 3vw, 28px)',
                    background: T.bgWhite,
                    border: `1px solid ${T.border}`,
                    borderLeft: '3px solid transparent',
                    borderRadius: T.r2,
                    display: 'flex',
                    gap: 16,
                    cursor: 'default',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderLeftColor = T.primaryBlue; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = T.sh3; }}
                  onMouseLeave={e => { e.currentTarget.style.borderLeftColor = 'transparent'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <span style={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: T.primaryBlue,
                      lineHeight: 1,
                      flexShrink: 0,
                      fontFamily: T.fontMono,
                      opacity: 0.4,
                    }}>{r.num}</span>
                    <div>
                      <h4 style={{ fontSize: 15, fontWeight: 700, color: T.atlasNight, margin: 0 }}>{r.title}</h4>
                      <p style={{ fontSize: 14, color: T.textSec, lineHeight: 1.6, margin: '8px 0 0 0' }}>{r.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FINAL CTA — Accent gradient
          ═══════════════════════════════════════════════════════════════════ */}
      <section id="contact" style={{
        background: `${T.deepBlue} url('/brand/cta-bg.jpg') no-repeat center center`,
        backgroundSize: 'cover',
        padding: 'clamp(56px, 10vw, 96px) 0',
        position: 'relative',
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <Reveal>
            <h2 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: 800,
              color: T.white,
              lineHeight: 1.12,
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
              {t('landing.cta.title')}
            </h2>
          </Reveal>

          <Reveal delay={0.15}>
            <p style={{
              fontSize: 17,
              color: 'rgba(255,255,255,0.8)',
              maxWidth: 560,
              margin: '24px auto 40px',
              lineHeight: 1.65,
            }}>
              {t('landing.cta.subtitle')}
            </p>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="cta-buttons" style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn-white"
                onClick={() => window.location.href = 'mailto:contact@rently.ma?subject=Demande de démo'}>
                {t('landing.cta.primary')} <span style={{ fontSize: 14 }}>→</span>
              </button>
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 32px',
                  fontSize: 15, fontWeight: 600, fontFamily: T.fontBase, background: 'transparent',
                  color: 'rgba(255,255,255,0.9)', border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: T.r2, cursor: 'pointer', transition: 'all 0.2s ease', textDecoration: 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              >
                <span style={{ color: '#25D366', display: 'inline-flex' }}>{Icons.whatsapp}</span> {t('landing.cta.whatsapp')}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FOOTER — Dark
          ═══════════════════════════════════════════════════════════════════ */}
      <footer style={{
        background: T.atlasNight,
        borderTop: `1px solid ${T.borderDark}`,
        padding: 'clamp(48px, 8vw, 72px) 0',
      }}>
        <div className="container">
          <div className="footer-grid" style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gap: 'clamp(32px, 5vw, 48px)',
          }}>
            {/* Brand */}
            <div>
              <img src="/brand/rently-lockup-white.svg" alt="Rently.ma"
                style={{ height: 36, width: 'auto', marginBottom: 20 }} />
              <p style={{ fontSize: 13, color: T.lightText, lineHeight: 1.65, margin: 0, maxWidth: 320 }}>
                {t('landing.footer.tagline')}
              </p>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: '20px 0 0 0' }}>© 2026 Rently.ma</p>
            </div>

            {/* Platform */}
            <div>
              <h4 style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1.2, margin: '0 0 16px 0' }}>{t('landing.footer.platformTitle')}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button onClick={() => setLoginModalOpen(true)} style={{
                  fontSize: 14, color: 'rgba(255,255,255,0.65)', background: 'transparent', border: 'none',
                  cursor: 'pointer', textAlign: 'start', padding: 0, fontFamily: T.fontBase,
                  transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.target.style.color = T.white}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.65)'}
                >{t('landing.footer.platformLogin')}</button>
                <a href="mailto:contact@rently.ma?subject=Demande de démo" style={{
                  fontSize: 14, color: 'rgba(255,255,255,0.65)', textDecoration: 'none', transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.target.style.color = T.white}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.65)'}
                >{t('landing.footer.platformDemo')}</a>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1.2, margin: '0 0 16px 0' }}>{t('landing.footer.contactTitle')}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" style={{
                  fontSize: 14, color: '#25D366', textDecoration: 'none', fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>{Icons.whatsapp} {t('landing.cta.whatsapp')}</a>
                <a href="mailto:contact@rently.ma" style={{ fontSize: 14, color: T.primaryBlue, textDecoration: 'none', fontWeight: 500 }}>contact@rently.ma</a>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{t('landing.footer.contactRegion')}</span>
              </div>
            </div>
          </div>

          {/* Footer bottom strip — language switch (secondary convenience) */}
          <div style={{
            marginTop: 40,
            paddingTop: 20,
            borderTop: '1px solid rgba(255,255,255,0.07)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}>
            <LangSwitch dark />
          </div>
        </div>
      </footer>

      {/* ═══════════════════════════════════════════════════════════════════
          LOGIN COMING-SOON MODAL
          ═══════════════════════════════════════════════════════════════════ */}
      {loginModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-soon-title"
          onClick={() => setLoginModalOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(7,21,33,0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'clamp(16px, 4vw, 32px)',
            animation: 'fadeIn 0.2s ease',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: T.bgWhite,
              borderRadius: T.r3,
              maxWidth: 460,
              width: '100%',
              padding: 'clamp(28px, 5vw, 40px)',
              boxShadow: '0 24px 80px rgba(7,21,33,0.35), 0 4px 12px rgba(7,21,33,0.18)',
              border: `1px solid ${T.border}`,
              position: 'relative',
              animation: 'scaleIn 0.25s var(--ease-smooth, ease)',
            }}
          >
            {/* Close X */}
            <button
              aria-label={t('landing.loginComingSoon.close')}
              onClick={() => setLoginModalOpen(false)}
              style={{
                position: 'absolute',
                top: 14,
                insetInlineEnd: 14,
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: 'none',
                background: 'transparent',
                color: T.textMuted,
                fontSize: 22,
                lineHeight: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; e.currentTarget.style.color = T.atlasNight; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textMuted; }}
            >
              ×
            </button>

            {/* Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              background: 'rgba(52,84,179,0.08)',
              border: `1px solid rgba(52,84,179,0.15)`,
              borderRadius: T.rFull,
              marginBottom: 18,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.primaryBlue, boxShadow: `0 0 6px ${T.primaryBlue}` }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: T.primaryBlue, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                {t('landing.loginComingSoon.badge')}
              </span>
            </div>

            {/* Title */}
            <h2
              id="login-soon-title"
              style={{
                fontSize: 'clamp(1.4rem, 3vw, 1.7rem)',
                fontWeight: 800,
                color: T.atlasNight,
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
                margin: '0 0 12px 0',
              }}
            >
              {t('landing.loginComingSoon.title')}
            </h2>

            {/* Body */}
            <p style={{
              fontSize: 15,
              color: T.textSec,
              lineHeight: 1.6,
              margin: '0 0 24px 0',
            }}>
              {t('landing.loginComingSoon.body')}
            </p>

            {/* CTAs */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setLoginModalOpen(false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '14px 22px',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: T.fontBase,
                  background: T.primaryBlue,
                  color: T.white,
                  border: 'none',
                  borderRadius: T.r1,
                  textDecoration: 'none',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = T.deepBlue; }}
                onMouseLeave={e => { e.currentTarget.style.background = T.primaryBlue; }}
              >
                <span style={{ display: 'inline-flex' }}>{Icons.whatsapp}</span>
                {t('landing.loginComingSoon.whatsapp')}
              </a>
              <a
                href="mailto:contact@rently.ma?subject=Demande%20d%27acc%C3%A8s%20Rently"
                onClick={() => setLoginModalOpen(false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '13px 22px',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: T.fontBase,
                  background: 'transparent',
                  color: T.atlasNight,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.r1,
                  textDecoration: 'none',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.primaryBlue; e.currentTarget.style.color = T.primaryBlue; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.atlasNight; }}
              >
                {t('landing.loginComingSoon.email')}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
